import { useEffect, useMemo, useState, type FormEvent, type CSSProperties } from "react";
import { previewNextFires } from "../../lib/cron-format.js";
import { useSetCronJobSchedule } from "./hooks.js";

type Freq =
  | "everyMinutes"
  | "everyHours"
  | "hourlyAt"
  | "daily"
  | "weekdays"
  | "weekly"
  | "monthly";

interface SimpleState {
  freq: Freq;
  everyN: number;
  minute: number;
  hour: number;
  dow: number;
  dom: number;
}

const DEFAULT_SIMPLE: SimpleState = {
  freq: "daily",
  everyN: 4,
  minute: 0,
  hour: 9,
  dow: 1,
  dom: 1,
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/**
 * Render an absolute instant as `YYYY-MM-DD HH:MM:SS` in the given IANA zone.
 * Built from formatToParts so it's locale-independent and matches the previous
 * ISO-ish layout, only wall-clock-correct for the host's zone instead of UTC.
 */
function formatFireInTz(d: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
}

function simpleToCron(s: SimpleState): string {
  switch (s.freq) {
    case "everyMinutes":
      return `*/${s.everyN} * * * *`;
    case "everyHours":
      return `0 */${s.everyN} * * *`;
    case "hourlyAt":
      return `${s.minute} * * * *`;
    case "daily":
      return `${s.minute} ${s.hour} * * *`;
    case "weekdays":
      return `${s.minute} ${s.hour} * * 1-5`;
    case "weekly":
      return `${s.minute} ${s.hour} * * ${s.dow}`;
    case "monthly":
      return `${s.minute} ${s.hour} ${s.dom} * *`;
  }
}

function cronToSimple(cron: string): SimpleState | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [m, h, dom, mon, dow] = parts as [string, string, string, string, string];
  if (mon !== "*") return null;

  const intOrNull = (v: string): number | null => {
    if (!/^\d+$/.test(v)) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  if (m.startsWith("*/") && h === "*" && dom === "*" && dow === "*") {
    const n = intOrNull(m.slice(2));
    if (n && n >= 1 && n <= 59) return { ...DEFAULT_SIMPLE, freq: "everyMinutes", everyN: n };
  }
  if (m === "0" && h.startsWith("*/") && dom === "*" && dow === "*") {
    const n = intOrNull(h.slice(2));
    if (n && n >= 1 && n <= 23) return { ...DEFAULT_SIMPLE, freq: "everyHours", everyN: n };
  }
  const mi = intOrNull(m);
  const hi = intOrNull(h);
  if (mi !== null && h === "*" && dom === "*" && dow === "*") {
    return { ...DEFAULT_SIMPLE, freq: "hourlyAt", minute: mi };
  }
  if (mi !== null && hi !== null && dom === "*" && dow === "*") {
    return { ...DEFAULT_SIMPLE, freq: "daily", minute: mi, hour: hi };
  }
  if (mi !== null && hi !== null && dom === "*" && dow === "1-5") {
    return { ...DEFAULT_SIMPLE, freq: "weekdays", minute: mi, hour: hi };
  }
  if (mi !== null && hi !== null && dom === "*" && /^[0-6]$/.test(dow)) {
    return { ...DEFAULT_SIMPLE, freq: "weekly", minute: mi, hour: hi, dow: Number(dow) };
  }
  const di = intOrNull(dom);
  if (mi !== null && hi !== null && di !== null && di >= 1 && di <= 31 && dow === "*") {
    return { ...DEFAULT_SIMPLE, freq: "monthly", minute: mi, hour: hi, dom: di };
  }
  return null;
}

type Tab = "simple" | "advanced";

export function ScheduleEditor({
  cronJobId,
  currentSchedule,
  agentTimezone,
  onClose,
}: {
  cronJobId: string;
  currentSchedule: string;
  agentTimezone?: string | null;
  onClose: () => void;
}) {
  const [schedule, setSchedule] = useState(currentSchedule);
  const setSched = useSetCronJobSchedule();

  // Preview in the host's zone (the host-agent reports it per snapshot) so the
  // fire times shown match what cron will actually do; fall back to the
  // browser's TZ when unknown — mirroring nextFireDate on CronRow.
  const tz = agentTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialSimple = useMemo(() => cronToSimple(currentSchedule), [currentSchedule]);
  const [tab, setTab] = useState<Tab>(initialSimple ? "simple" : "advanced");
  const [simple, setSimple] = useState<SimpleState>(initialSimple ?? DEFAULT_SIMPLE);

  // Whenever simple-tab inputs change, sync the canonical `schedule`.
  const updateSimple = (patch: Partial<SimpleState>) => {
    const next = { ...simple, ...patch };
    setSimple(next);
    setSchedule(simpleToCron(next));
  };

  // Switching to Simple: if the current raw cron parses, snap Simple to it.
  const switchToSimple = () => {
    const parsed = cronToSimple(schedule);
    if (parsed) {
      setSimple(parsed);
    }
    setTab("simple");
  };

  // Close on Escape — overlay-click already closes; keyboard users get parity.
  // Document-level so it fires regardless of which control inside has focus.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const preview = previewNextFires(schedule, 5, tz);
  const simpleCanRepresent = cronToSimple(schedule) !== null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!preview.ok) return;
    try {
      await setSched.mutateAsync({ cronJobId, schedule });
      onClose();
    } catch {
      // surfaced via setSched.error
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-editor-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={onSubmit}
        className="panel modal-form"
        style={{
          margin: 0,
          padding: 20,
          width: 520,
          maxWidth: "100%",
          display: "grid",
          gap: 12,
        }}
      >
        <div id="schedule-editor-title" style={{ fontWeight: 700, fontSize: 16 }}>
          Edit schedule
        </div>

        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)" }}>
          <TabButton active={tab === "simple"} onClick={switchToSimple}>
            Simple
          </TabButton>
          <TabButton active={tab === "advanced"} onClick={() => setTab("advanced")}>
            Advanced
          </TabButton>
        </div>

        {tab === "simple" ? (
          <SimpleTab simple={simple} onChange={updateSimple} schedule={schedule} canRepresent={simpleCanRepresent} />
        ) : (
          <AdvancedTab schedule={schedule} onChange={setSchedule} />
        )}

        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          {!preview.ok ? "Invalid:" : preview.times.length > 0 ? `Next 5 fires (${tz}):` : "Schedule:"}
        </div>
        {!preview.ok ? (
          <div style={{ color: "var(--err)", fontSize: 12 }}>{preview.error}</div>
        ) : preview.times.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: "var(--text)" }}>
            {preview.times.map((t) => (
              <li key={t.toISOString()}>{formatFireInTz(t, tz)}</li>
            ))}
          </ul>
        ) : (
          // A valid schedule with nothing to preview (`@reboot`). Saving stays
          // enabled — `preview.ok` is what gates the submit button.
          <div style={{ fontSize: 12, color: "var(--text)" }}>{preview.note}</div>
        )}

        {setSched.error && (
          <div style={{ color: "var(--err)", fontSize: 12 }}>
            {String((setSched.error as Error).message ?? setSched.error)}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              color: "var(--muted)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "6px 12px",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!preview.ok || setSched.isPending}
            style={{
              background: "var(--accent)",
              color: "#0e1116",
              border: "none",
              borderRadius: 4,
              padding: "6px 12px",
              fontSize: 13,
              fontWeight: 600,
              cursor: preview.ok ? "pointer" : "not-allowed",
              opacity: preview.ok ? 1 : 0.6,
            }}
          >
            {setSched.isPending ? "Saving…" : "Save (apply within 30s)"}
          </button>
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)" }}>
          The dashboard enqueues a command; the host-agent applies it on its
          next poll (within ~30 seconds) and the crontab is updated atomically.
        </div>
      </form>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "transparent",
        color: active ? "var(--text)" : "var(--muted)",
        border: "none",
        borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
        padding: "8px 12px",
        marginBottom: -1,
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function SimpleTab({
  simple,
  onChange,
  schedule,
  canRepresent,
}: {
  simple: SimpleState;
  onChange: (p: Partial<SimpleState>) => void;
  schedule: string;
  canRepresent: boolean;
}) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {!canRepresent && (
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          The current expression isn't representable in Simple form. Pick a
          frequency below to overwrite it, or switch to Advanced.
        </div>
      )}

      <label style={{ display: "grid", gap: 4 }}>
        <span style={{ color: "var(--muted)", fontSize: 12 }}>Run</span>
        <select
          value={simple.freq}
          onChange={(e) => onChange({ freq: e.target.value as Freq })}
          style={inputStyle}
        >
          <option value="everyMinutes">Every N minutes</option>
          <option value="everyHours">Every N hours</option>
          <option value="hourlyAt">Hourly, at a specific minute</option>
          <option value="daily">Daily, at a specific time</option>
          <option value="weekdays">Weekdays (Mon–Fri), at a specific time</option>
          <option value="weekly">Weekly, on a specific day &amp; time</option>
          <option value="monthly">Monthly, on a day of the month &amp; time</option>
        </select>
      </label>

      {simple.freq === "everyMinutes" && (
        <Field label="Every (minutes)">
          <NumberInput
            min={1}
            max={59}
            value={simple.everyN}
            onChange={(n) => onChange({ everyN: n })}
          />
        </Field>
      )}

      {simple.freq === "everyHours" && (
        <Field label="Every (hours)">
          <NumberInput
            min={1}
            max={23}
            value={simple.everyN}
            onChange={(n) => onChange({ everyN: n })}
          />
        </Field>
      )}

      {simple.freq === "hourlyAt" && (
        <Field label="Minute past the hour">
          <NumberInput
            min={0}
            max={59}
            value={simple.minute}
            onChange={(n) => onChange({ minute: n })}
          />
        </Field>
      )}

      {(simple.freq === "daily" ||
        simple.freq === "weekdays" ||
        simple.freq === "weekly" ||
        simple.freq === "monthly") && (
        <Field label="Time (HH:MM)">
          <TimeInput
            hour={simple.hour}
            minute={simple.minute}
            onChange={(h, m) => onChange({ hour: h, minute: m })}
          />
        </Field>
      )}

      {simple.freq === "weekly" && (
        <Field label="Day of week">
          <select
            value={simple.dow}
            onChange={(e) => onChange({ dow: Number(e.target.value) })}
            style={inputStyle}
          >
            {DAYS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        </Field>
      )}

      {simple.freq === "monthly" && (
        <Field label="Day of month">
          <NumberInput
            min={1}
            max={31}
            value={simple.dom}
            onChange={(n) => onChange({ dom: n })}
          />
        </Field>
      )}

      <div style={{ fontSize: 11, color: "var(--muted)" }}>
        cron: <code style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{schedule}</code>
      </div>
    </div>
  );
}

function AdvancedTab({
  schedule,
  onChange,
}: {
  schedule: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={{ color: "var(--muted)", fontSize: 12 }}>
        Cron expression (5 fields)
      </span>
      <input
        type="text"
        value={schedule}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoFocus
        style={{
          ...inputStyle,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      />
    </label>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={{ color: "var(--muted)", fontSize: 12 }}>{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  min,
  max,
  value,
  onChange,
}: {
  min: number;
  max: number;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, Math.floor(n))));
      }}
      style={inputStyle}
    />
  );
}

function TimeInput({
  hour,
  minute,
  onChange,
}: {
  hour: number;
  minute: number;
  onChange: (h: number, m: number) => void;
}) {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    <input
      type="time"
      value={`${pad(hour)}:${pad(minute)}`}
      onChange={(e) => {
        const parts = e.target.value.split(":");
        const h = Number(parts[0]);
        const m = Number(parts[1]);
        if (Number.isFinite(h) && Number.isFinite(m)) onChange(h, m);
      }}
      style={inputStyle}
    />
  );
}

const inputStyle: CSSProperties = {
  background: "var(--bg)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: 4,
  padding: "8px 10px",
  fontSize: 14,
};
